<template>
    <Modal :title="dialogTitle" :visible.sync="visible" width="700px" :close-on-click-modal="false" append-to-body>
        <el-form ref="form" :rules="rules" :model="form" label-width="120px">
            <el-form-item prop="code" :label="$t('resource.funcCode')">
                <el-input
                    v-model.trim="form.code"
                    style="width: 400px"
                    maxlength="20"
                    :disabled="isEdit"
                    :placeholder="$t('resource.funcCodeNotEmtry')"
                ></el-input>
            </el-form-item>
            <el-form-item prop="name" :label="$t('resource.func')">
                <el-input v-model.trim="form.name" style="width: 400px" maxlength="30"></el-input>
            </el-form-item>
            <el-form-item prop="nameZh" :label="$t('resource.nameZh')">
                <el-input v-model.trim="form.nameZh" style="width: 400px" maxlength="50"></el-input>
            </el-form-item>
            <el-form-item prop="nameEn" :label="$t('resource.nameEn')">
                <el-input v-model.trim="form.nameEn" style="width: 400px" maxlength="50"></el-input>
            </el-form-item>
            <el-form-item prop="extraCode" :label="$t('resource.extraCode')">
                <el-input v-model.trim="form.extraCode" style="width: 400px" maxlength="20"></el-input>
            </el-form-item>
        </el-form>

        <div class="url-section">
            <div class="url-header">
                <span>{{ $t('resource.urlList') }}</span>
                <span class="url-count">({{ form.urls.length }})</span>
                <el-button
                    type="primary"
                    size="mini"
                    @click="showUrlSelector = !showUrlSelector"
                >{{ $t('resource.addUrl') }}</el-button>
            </div>

            <div class="url-selector" v-if="showUrlSelector">
                <el-input
                    v-model="urlSearchVal"
                    size="small"
                    clearable
                    :placeholder="$t('resource.selectUrlPlaceholder')"
                    @input="filterUrls"
                ></el-input>
                <div class="url-options" v-loading="urlLoading">
                    <ul v-if="filteredUrlArray.length > 0">
                        <li
                            v-for="item in filteredUrlArray"
                            :key="item.id"
                            class="uk-flex uk-flex-middle"
                            @click="addUrl(item)"
                        >
                            <el-checkbox :value="isUrlSelected(item.url)" disabled></el-checkbox>
                            <span class="url-text" :title="item.url">{{ item.url }}</span>
                            <span class="url-method">{{ item.method }}</span>
                        </li>
                    </ul>
                    <el-empty v-else :description="$t('resource.noData')" :image-size="40"></el-empty>
                </div>
            </div>

            <el-table
                v-if="form.urls.length > 0"
                :data="form.urls"
                border
                size="small"
                style="width: 100%"
                max-height="200"
            >
                <el-table-column prop="url" :label="$t('resource.url')" show-overflow-tooltip></el-table-column>
                <el-table-column prop="method" :label="$t('resource.method')" width="100"></el-table-column>
                <el-table-column :label="$t('resource.optare')" width="60" align="center">
                    <template #default="{ $index }">
                        <i
                            class="el-icon-delete remove-icon"
                            @click="removeUrl($index)"
                        ></i>
                    </template>
                </el-table-column>
            </el-table>
            <div v-else class="no-url-tip">{{ $t('resource.noUrlSelected') }}</div>
        </div>

        <div slot="footer">
            <el-button type="primary" @click="handleClose">{{ $t('public.btn_close') }}</el-button>
            <el-button type="primary" :loading="saving" @click="handleSave">{{ $t('public.btn_ok') }}</el-button>
        </div>
    </Modal>
</template>

<script>
import { functionDetail, functionSave, functionOperateUpdate, urlResourceList } from '@/api/modules/resoure.js';

export default {
    name: 'FunctionDialog',
    data() {
        return {
            visible: false,
            saving: false,
            isEdit: false,
            showUrlSelector: false,
            urlLoading: false,
            urlSearchVal: '',
            urlArray: [],
            filteredUrlArray: [],
            treeRow: {},
            form: {
                code: '',
                name: '',
                nameZh: '',
                nameEn: '',
                extraCode: '',
                urls: []
            }
        };
    },
    computed: {
        dialogTitle() {
            return this.isEdit ? this.$t('resource.editFunction') : this.$t('resource.addFunction');
        },
        rules() {
            return {
                code: [{ required: true, message: this.$t('resource.funcCodeNotEmtry'), trigger: 'blur' }],
                name: [{ required: true, message: this.$t('integration.function_cannot_be_empty'), trigger: 'blur' }],
                nameZh: [{ required: true, message: this.$t('resource.nameZhNotEmpty'), trigger: 'blur' }],
                nameEn: [{ required: true, message: this.$t('resource.nameEnNotEmpty'), trigger: 'blur' }]
            };
        }
    },
    methods: {
        open(row, treeRow) {
            this.treeRow = treeRow || {};
            this.isEdit = !!row && !!row.code;
            this.resetForm();
            if (this.isEdit) {
                this.loadDetail(row);
            }
            this.loadUrlResources();
            this.visible = true;
        },
        resetForm() {
            this.form = {
                code: '',
                name: '',
                nameZh: '',
                nameEn: '',
                extraCode: '',
                urls: []
            };
            this.showUrlSelector = false;
            this.urlSearchVal = '';
            if (this.$refs.form) {
                this.$refs.form.resetFields();
            }
        },
        /** 根据 treeRow 类型决定用 pageId 还是 subId */
        getQueryParams() {
            if (this.treeRow.parentCode) {
                return { pageId: null, subId: this.treeRow.id ? String(this.treeRow.id) : null };
            }
            return { pageId: this.treeRow.id ? String(this.treeRow.id) : null, subId: null };
        },
        loadDetail(row) {
            const params = this.getQueryParams();
            functionDetail({ code: row.code, ...params }).then(({ result }) => {
                if (result) {
                    this.form.code = result.code || '';
                    this.form.name = result.name || '';
                    this.form.nameZh = result.nameZh || result.name || '';
                    this.form.nameEn = result.nameEn || result.name || '';
                    this.form.extraCode = result.extraCode || '';
                    this.form.urls = (result.urls || []).map(item => ({
                        id: item.id,
                        url: item.url,
                        method: item.method,
                        pathParam: item.pathParam
                    }));
                }
            });
        },
        loadUrlResources() {
            this.urlLoading = true;
            urlResourceList({ iDisplayStart: 0, iDisplayLength: 10000 })
                .then(({ result }) => {
                    this.urlArray = (result || []).map(item => ({
                        id: item.id,
                        url: item.url,
                        method: item.method,
                        pathParam: item.pathParam
                    }));
                    this.filteredUrlArray = [...this.urlArray];
                })
                .finally(() => {
                    this.urlLoading = false;
                });
        },
        filterUrls() {
            if (!this.urlSearchVal) {
                this.filteredUrlArray = [...this.urlArray];
            } else {
                const keyword = this.urlSearchVal.toLowerCase();
                this.filteredUrlArray = this.urlArray.filter(item =>
                    item.url.toLowerCase().includes(keyword)
                );
            }
        },
        isUrlSelected(url) {
            return this.form.urls.some(item => item.url === url);
        },
        addUrl(item) {
            if (this.isUrlSelected(item.url)) {
                this.$message.warning(this.$t('resource.urlAlreadyExists'));
                return;
            }
            this.form.urls.push({
                url: item.url,
                method: item.method,
                pathParam: item.pathParam
            });
        },
        removeUrl(index) {
            this.form.urls.splice(index, 1);
        },
        handleSave() {
            this.$refs.form.validate(valid => {
                if (!valid) return;
                if (this.form.urls.length === 0) {
                    this.$message.warning(this.$t('resource.urlNotEmpty'));
                    return;
                }
                this.saving = true;
                const params = this.getQueryParams();
                // 发送 snake_case 键，后端 FieldDeserializationStrategy 依赖 @FieldNaming 映射
                const payload = {
                    code: this.form.code,
                    name: this.form.name,
                    nameZh: this.form.nameZh,
                    nameEn: this.form.nameEn,
                    extraCode: this.form.extraCode,
                    pageId: params.pageId,
                    subPageId: params.subId,
                    urls: this.form.urls.map(item => ({
                        url: item.url,
                        method: item.method,
                        pathParam: item.pathParam
                    }))
                };
                const saveFn = this.isEdit ? functionOperateUpdate : functionSave;
                saveFn(payload)
                    .then(({ result, state }) => {
                        if (result === 200) {
                            this.$message.success(this.$t('public.tip_save_succ'));
                            this.visible = false;
                            this.$emit('saved');
                        } else if (result === 406) {
                            this.$message.error(this.$t('integration.url_already_exists'));
                        } else {
                            this.$message.error(state.msg || 'Error');
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            });
        },
        handleClose() {
            this.visible = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.url-section {
    margin-top: 15px;
    padding: 0 20px;

    .url-header {
        display: flex;
        align-items: center;
        margin-bottom: 10px;

        span {
            font-weight: bold;
            margin-right: 5px;
        }
        .url-count {
            color: #909399;
            font-weight: normal;
            margin-right: 15px;
        }
    }

    .url-selector {
        margin-bottom: 15px;
        padding: 10px;
        background: #f5f7fa;
        border-radius: 4px;

        .url-options {
            max-height: 200px;
            overflow-y: auto;
            margin-top: 8px;

            ul {
                list-style: none;
                padding: 0;
                margin: 0;

                li {
                    height: 32px;
                    padding: 0 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    border-bottom: 1px solid #e8e8e8;

                    &:hover {
                        background: #ecf5ff;
                    }
                    .url-text {
                        flex: 1;
                        margin-left: 8px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .url-method {
                        width: 70px;
                        text-align: right;
                        color: #909399;
                        font-size: 12px;
                        margin-left: 8px;
                    }
                }
            }
        }
    }

    .no-url-tip {
        text-align: center;
        color: #909399;
        padding: 20px;
        border: 1px dashed #dcdfe6;
        border-radius: 4px;
    }

    .remove-icon {
        color: var(--primary-color);
        cursor: pointer;
        font-size: 16px;

        &:hover {
            color: #5a7fa8;
        }
    }
}
</style>
